import { DisplayScale, NumberFormatConfig, SCALE_SUFFIX_CONFIG } from './numberScale'

const getExcelScaleSuffix = (displayScale?: DisplayScale, showScaleSuffix?: boolean): string => {
  const trailingCommas: Partial<Record<DisplayScale, string>> = {
    [DisplayScale.thousands]: ',',
    [DisplayScale.millions]: ',,'
  }
  const commas = displayScale ? trailingCommas[displayScale] ?? '' : ''
  if (!commas) return ''
  if (showScaleSuffix === false) return commas
  const { suffix } = SCALE_SUFFIX_CONFIG[displayScale!]
  return suffix ? `${commas}"${suffix}"` : commas
}

/**
 * Builds an Excel-native number format string (e.g. `# ### ### ##0,,"M"`) from
 * a `NumberFormatConfig`. Scaling (thousands/millions) is applied via Excel's
 * trailing-comma format trick, so the underlying cell value stays raw and
 * calculable — only the display is scaled.
 *
 * Shared by the custom-reports ExcelJS export pipeline and the financial
 * statements ExcelJS export pipeline.
 */
export const buildNumberFormat = (numberFormat: NumberFormatConfig, isPercentage: boolean): string => {
  const decimals = numberFormat.decimals ?? 0
  const decimalPart = decimals > 0 ? `.${'0'.repeat(decimals)}` : ''

  if (isPercentage) {
    const base = `# ### ### ##0${decimalPart}%`
    return `${base};-${base};`
  }

  const scaleSuffix = getExcelScaleSuffix(numberFormat.displayScale, numberFormat.showScaleSuffix)
  const base = `# ### ### ##0${decimalPart}${scaleSuffix}`
  return `${base};-${base};`
}

/**
 * Background colors for financial-statement/report column types, keyed by
 * the `{columnType}-column` class name used across the app (e.g.
 * `column.className` on a `StatementColumn`). Single source of truth shared
 * by `createExcelStyles()` (native ag-Grid export) and the ExcelJS-based
 * per-cell metadata providers.
 *
 * Note: Excel doesn't support alpha transparency, so colors are pre-blended
 * with a white background (mirrors the on-screen rgba colors).
 */
export const COLUMN_TYPE_BG_COLORS: Record<string, string> = {
  'budget-column': '#eff2f7', // rgba(201, 211, 228, 0.3)
  'forecast-column': '#e1eeff', // rgba(156, 197, 255, 0.3)
  'forecast-column-bright': '#f2f7ff', // rgba(210, 228, 255, 0.3)
  'comparativePeriod-column': '#e9f4f9', // rgba(180, 217, 236, 0.3)
  'actuals-cumulative-column': '#f5f5f5', // rgba(243, 243, 243, 0.8)
  'forecast-cumulative-column': '#f8fff1', // rgba(246, 255, 237, 0.8)
  'budget-cumulative-column': '#ccf9ff', // rgba(191, 247, 255, 0.8)
  'comparativePeriod-cumulative-column': '#c6edf2', // rgba(184, 232, 239, 0.8)
  'diff-column': '#c3e2ff', // rgba(180, 219, 255, 0.8)
  'comparative-diff-column': '#bdeaf0' // rgba(173, 229, 236, 0.8)
}
